// src/Pages/OtaYhteytta/OtaYhteytta.jsx
import React, { useMemo, useRef, useState } from "react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTheme } from "../../context/ThemeContext";
import NeonButton from "../../components/ResponsiveButton/NeonButton.jsx";
import { Send, CheckCircle2 } from "lucide-react";
import emailjs from "emailjs-com";
import RecaptchaField from "../../components/RecaptchaField/RecaptchaField";

/* ✅ HERO-kuvat */
import heroLight1x from "../../assets/otayhteyttä.webp";
import heroLight2x from "../../assets/otayhteyttä.webp";
import heroDark1x  from "../../assets/contact-dark-1280.webp";
import heroDark2x  from "../../assets/contact-dark-1280.webp"; // jos sinulla on 2x-versio, päivitä tähän 2560px tms.

/* ========= Brändi & yhteystiedot ========= */
const BRAND = "Buukkeri";
const BRAND_DOMAIN = "buukkeri.fi";
const SUPPORT_MAIL = `info@${BRAND_DOMAIN}`;

/* ========= EmailJS-ympäristö (CRA) ========= */
const EMAILJS_USER_ID     = process.env.REACT_APP_EMAILJS_USER_ID;
const EMAILJS_SERVICE_ID  = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID_CONTACT = process.env.REACT_APP_EMAILJS_TEMPLATE_ID_CONTACT; // unified-template

const OWNER_OVERRIDE_EMAIL =
  (process.env.REACT_APP_OWNER_OVERRIDE_EMAIL || SUPPORT_MAIL).trim();

const BYPASS_EMAILJS =
  String(process.env.REACT_APP_BYPASS_EMAILJS || "").toLowerCase() === "true";
const BYPASS_RECAPTCHA =
  String(process.env.REACT_APP_BYPASS_RECAPTCHA || "").toLowerCase() === "true";

/* ✅ EmailJS-lähetys apuri */
const safeEmailSend = async (serviceId, templateId, params) => {
  if (BYPASS_EMAILJS) return { bypassed: true };

  if (!EMAILJS_USER_ID || !serviceId || !templateId) {
    const msg = "EmailJS .env puuttuu: USER_ID / SERVICE_ID / TEMPLATE_ID_CONTACT";
    console.error(msg, { EMAILJS_USER_ID, serviceId, templateId });
    throw new Error(msg);
  }

  const clean = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v == null) return;
    // EmailJS/Handlebars käsittelee booleanit kökösti -> pakotetaan merkkijonoiksi.
    // true => "1", false => "" (falsy). Muut arvot edelleen merkkijonoiksi.
    if (typeof v === "boolean") {
      clean[k] = v ? "1" : "";
    } else {
      clean[k] = typeof v === "string" ? v : String(v);
    }
  });

  try { emailjs.init(EMAILJS_USER_ID.trim()); } catch {}

  const sendP = emailjs
    .send(serviceId.trim(), templateId.trim(), clean, EMAILJS_USER_ID.trim())
    .then(r => {
      console.log("EmailJS OK:", r?.status || r);
      return r;
    })
    .catch(err => {
      console.error("EmailJS ERROR:", err);
      throw err;
    });

  // 15 s timeout
  const timeoutP = new Promise((_, rej) =>
    setTimeout(() => rej(new Error("EmailJS timeout (mahd. CORS estää kutsun)")), 15000)
  );

  return Promise.race([sendP, timeoutP]);
};

export default function OtaYhteytta() {
  const { theme } = useTheme();

  /* Lomakkeen tila */
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [values, setValues] = useState({
    topic: "",
    name: "",
    email: "",
    phone: "",
    city: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const msgRef = useRef(null);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  /* Honeypot + aikaraja */
  const [hp, setHp] = useState("");
  const [startedAt] = useState(() => Date.now());

  /* Parallax HERO */
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 300], [0, 50]);

  const heroOverlay =
    theme === "dark"
      ? "from-[#02030A]/5 via-[#02030A]/80 to-[#02030A]/5"
      : "from-white/30 via-black/70 to-black/5";

  const cardBg =
    theme === "dark" ? "bg-[#0c0c12]/70 ring-white/10" : "bg-lemon/70 ring-black/10";

// syötekenttien perusluokat
const inputBase =
  "w-full h-10 rounded-lg px-3 " +
  "bg-white dark:bg-black/40 " +
  "border border-black/10 dark:border-white/10 " +
  "text-neutral-900 dark:text-white " +                     // <-- näkyvä teksti molempiin teemoihin
  "placeholder:text-neutral-500 dark:placeholder:text-white/60 " +
  "focus:outline-none focus:ring-2 focus:ring-primary/60";

// labelin perusluokat
const labelBase =
  "block text-black/90 dark:text-white/90 font-customFont2 xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm mb-1 font-bold";

  // textarea käyttää omaa basea (ei kiinteää h-10)
const textareaBase =
  "w-full rounded-lg px-3 py-3 " +
  "bg-white dark:bg-black/40 " +
  "border border-black/10 dark:border-white/10 " +
  "text-neutral-900 dark:text-white " +
  "placeholder:text-neutral-500 dark:placeholder:text-white/60 " +
  "focus:outline-none focus:ring-2 focus:ring-primary/60";


  /* Validaattori */
  const validate = useMemo(
    () => (vals) => {
      const e = {};
      if (!vals.topic) e.topic = "Valitse, mitä yhteydenotto koskee.";
      if (!vals.name.trim()) e.name = "Nimi on pakollinen.";
      if (!vals.email.trim()) e.email = "Sähköposti on pakollinen.";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email))
        e.email = "Sähköpostiosoite ei ole kelvollinen.";
      if (vals.phone && !/^[0-9+()\-\s]{6,}$/.test(vals.phone))
        e.phone = "Puhelinnumero ei ole kelvollinen.";
      if (!vals.message.trim() || vals.message.trim().length < 10)
        e.message = "Viesti on liian lyhyt – kirjoita vähintään 10 merkkiä.";
      return e;
    },
    []
  );

  // komponentin sisällä, ennen returnia:
const isTopicEmpty = !values.topic;


  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (formErrors[name]) setFormErrors((er) => ({ ...er, [name]: undefined }));
  };

  /* ✅ Onnistumisviestiin scrollaus (erityisesti mobiilissa) */
  const scrollToMessage = React.useCallback(() => {
    const el = msgRef.current;
    if (!el || typeof window === "undefined") return;
    const NAV_OFFSET = 88; // arviolta header/navin korkeus
    const y = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
    window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    try { el.focus({ preventScroll: true }); } catch {}
  }, []);


  const onSubmitForm = async (e) => {
    e.preventDefault();

    if (!EMAILJS_USER_ID || !EMAILJS_SERVICE_ID || !TEMPLATE_ID_CONTACT) {
      setFormErrors(er => ({ ...er, form: "EmailJS-asetukset puuttuvat (REACT_APP_EMAILJS_USER_ID / REACT_APP_EMAILJS_SERVICE_ID / REACT_APP_EMAILJS_TEMPLATE_ID_CONTACT)." }));
      return;
    }

    // reCAPTCHA (ohitettu jos BYPASS_RECAPTCHA=true)
    const recaptchaActive = !BYPASS_RECAPTCHA;
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (recaptchaActive && !siteKey) {
      setFormErrors((er) => ({ ...er, form: "reCAPTCHA-avain puuttuu (.env)." }));
      return;
    }
    if (recaptchaActive && !recaptchaValue) {
      setFormErrors((er) => ({ ...er, form: "Vahvista reCAPTCHA ennen lähettämistä." }));
      return;
    }

    if (hp) return; // botti täytti honeypotin
    if (Date.now() - startedAt < 1200) {
      setFormErrors((er) => ({ ...er, form: "Lähetys liian nopea. Yritä uudelleen." }));
      return;
    }

    const er = validate(values);
    setFormErrors(er);
    if (Object.keys(er).length) return;

    setBusy(true);
    try {

      // ✅ Esilasketut otsikot + preheader
      const subjectOwner   = `Uusi yhteydenotto: ${values.topic || "-"}`;
      const preheaderOwner = `Uusi yhteydenotto: ${values.topic} – ${values.name}.`;
      const now = new Date();

      // ✅ Yhteiset parametrit
      const common = {
        topic: values.topic,
        user_name: values.name,
        user_email: values.email,
        user_phone: values.phone || "-",
        user_city: values.city || "-",
        user_message: values.message,
        reply_to: values.email, // ylläpito voi vastata suoraan asiakkaalle
        to_email: "",           // asetetaan alla
        submitted_at: now.toLocaleString("fi-FI"),
        site_url:
          typeof window !== "undefined"
            ? window.location.href
            : `https://${BRAND_DOMAIN}/ota-yhteytta`,
        // Ei hero-kuvaa
        header_image_url: "",
        year: String(now.getFullYear()),
        recaptcha_token: recaptchaValue || "",
        subject: "",
        preheader: "",
      };

      const ownerTo = OWNER_OVERRIDE_EMAIL || SUPPORT_MAIL;

      // 📧 Lähetetään VAIN ylläpidolle
      await safeEmailSend(EMAILJS_SERVICE_ID, TEMPLATE_ID_CONTACT, {
        ...common,
        is_owner: true,
        to_email: ownerTo,
        reply_to: values.email,
        subject: subjectOwner,
        preheader: preheaderOwner,
      });

      setSent(true);
      // Sulje mobiilinäppis ja anna DOM:in piirtää viestikomponentti
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
      await new Promise((r) => setTimeout(r, 80));
      scrollToMessage();

      // reset
      setValues({
        topic: "",
        name: "",
        email: "",
        phone: "",
        city: "",
        message: "",
      });

      setRecaptchaValue(null);
      recaptchaRef.current?.reset?.();
    } catch (err) {
      setFormErrors((er) => ({
        ...er,
        form:
          "Lähetys epäonnistui. " +
          (err?.message || "Yritä hetken päästä uudelleen."),
      }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Ota yhteyttä – {BRAND}</title>
        {/*
          Meta description tulee palvelimelta (BBX SEO CORE).
          Jätetään Helmetistä pois, jotta ei synny duplikaattia.
        */}
      </Helmet>

      {/* HERO */}
      <header className="relative w-full h-[50vh] min-h-[260px] overflow-hidden mt-20">
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <picture className="absolute inset-0">
            {theme === "dark" ? (
              <source srcSet={`${heroDark1x} 1x, ${heroDark2x} 2x`} type="image/webp" />
            ) : (
              <source srcSet={`${heroLight1x} 1x, ${heroLight2x} 2x`} type="image/webp" />
            )}
            <img
              src={theme === "dark" ? heroDark1x : heroLight1x}
              alt=""
              className="h-full w-full object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
            />
          </picture>
        </motion.div>

        {/* kiilto + overlay */}
        <motion.div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(120deg, rgba(255,255,255,0.07) 20%, transparent 80%)," +
              "linear-gradient(300deg, rgba(255,255,255,0.06) 15%, transparent 85%)",
            maskImage:
              "radial-gradient(120% 100% at 50% 60%, black 55%, transparent 25%)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.0, 1, 0.6] }}
          transition={{ duration: 2.2, ease: "easeOut" }}
          aria-hidden
        />
        <motion.div
          className={`absolute inset-0 bg-gradient-to-b ${heroOverlay}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          aria-hidden
        />

        {/* Tekstit */}
        <div className="relative z-10 flex h-full items-center">
          <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8">
           

            <motion.h1
              className="font-customFont2 dark:text-shadow-lg text-center text-electric dark:text-primary text-5xl sm:text-6xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-6xl font-bold leading-tight"
                 initial={{ y: 12, opacity: 0 }}
                 animate={{ y: 0, opacity: 1 }}
                 transition={{ duration: 0.5 }}
               >
              Ota yhteyttä
            </motion.h1>

            <motion.p
               className="mt-3 dark:text-white/80 text-white/80 font-customFont1 text-lg text-center text-shadow-lg"
                  initial={{ y: 12, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.05 }}
                >
              Valitse aihe ja kerro lyhyesti – palaamme yleensä saman arkipäivän aikana.
            </motion.p>

            <motion.div
              className="mt-6 flex items-center justify-center gap-3 flex-wrap"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
             
            </motion.div>
          </div>
        </div>
      </header>

      {/* SISÄLTÖ */}
      <main className="relative w-full pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <section className="grid grid-cols-1 md:grid-cols-2 mdd:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6 mt-8">
            {/* Lomakekortti */}
            <article className={`md:col-span-2 rounded-2xl ring-1 ${cardBg} backdrop-blur-md shadow-xl`}>
              <div className="p-6 sm:p-8">
                <h2 className="font-customFont2 dark:text-white/90 text-black/90 text-lg md:text-2xl font-semibold mb-4">
                  Yhteydenottolomake
                </h2>
                {/* <p className="dark:text-white/80 text-black/80 font-customFont1 text-sm sm:text-base mb-6">
                  Vastamme yleensä saman arkipäivän aikana.
                </p> */}

                {formErrors.form && (
                  <div className="mb-4 rounded-xl bg-red-500/15 ring-1 ring-red-500/40 p-3 text-red-200">
                    {formErrors.form}
                  </div>
                )}

                <div role="status" aria-live="polite">
                  {sent && (
                    <div
                    ref={msgRef}
                    tabIndex={-1}
                    role="alert"
                    className="mb-6 flex items-start gap-3 rounded-xl bg-primary/10 ring-1 ring-primary/40 p-4 dark:text-white/80 text-black/80 font-customFont1 xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm"
                    >
                      <CheckCircle2 className="h-5 w-5 shrink-0 mt-0.5" />
                      <div>
                        <p>Kiitos yhteydenotosta!</p>
                        <p>Palaamme asiaan mahdollisimman pian.</p>
                      </div>
                    </div>
                  )}
                </div>

                <form onSubmit={onSubmitForm} noValidate>
                  {/* Honeypot */}
                  <input
                    name="website"
                    value={hp}
                    onChange={(e) => setHp(e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    className="hidden"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* AIHE */}
                    <div className="sm:col-span-2">
                      <label htmlFor="topic" className={labelBase}>
                        Aihe (mitä yhteydenotto koskee?) *
                      </label>

                      <div className="relative">
                        <select
                          id="topic"
                          name="topic"
                          value={values.topic}
                          onChange={handleChange}
                          className={`
                            block w-full h-12 rounded-xl appearance-none px-4 pr-10 transition
                            bg-white/95 dark:bg-white/[0.06]
                            border border-black/10 dark:border-white/20
                            text-black/90 dark:text-white/90
                            ${isTopicEmpty ? "text-black/70 dark:text-white/70" : ""}
                            shadow-sm dark:shadow-[0_0_0_1px_rgba(255,255,255,0.04)]
                            focus:outline-none focus:ring-2 focus:ring-primary/60 focus:border-primary/60
                            hover:bg-white dark:hover:bg-white/[0.09]
                          `}
                          aria-invalid={!!formErrors.topic}
                          aria-describedby={formErrors.topic ? "topic-err" : undefined}
                        >
                        {/* placeholder – hidden: ei näy listassa, vain valittuna */}
                          <option value="" disabled hidden>— Valitse —</option>

                          <option value="Tarjouspyyntö – varaus">Tarjouspyyntö – varaus</option>
                          <option value="Reklamaatio – palaute">Reklamaatio – palaute</option>
                          <option value="Yhteistyö – media">Yhteistyö – media</option>
                          <option value="Esiintyjäkortti">Esiintyjäkortti</option>
                          <option value="Muu kysymys">Muu kysymys</option>

                        </select>

                        {/* nuoli selkeämmäksi darkissa */}
                        <svg
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4
                                    text-black/60 dark:text-white/80 opacity-90"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                          aria-hidden="true"
                        >
                          <path d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.94l3.71-3.71a.75.75 0 1 1 1.06 1.06l-4.24 4.24a.75.75 0 0 1-1.06 0L5.21 8.29a.75.75 0 0 1 .02-1.08z" />
                        </svg>
                      </div>

                      {formErrors.topic && (
                        <p id="topic-err" className="mt-1 text-xs text-pink-400">{formErrors.topic}</p>
                      )}
                    </div>

                    {/* Nimi */}
                    <div>
                      <label htmlFor="name" className={labelBase}>Nimi *</label>
                      <motion.input
                        id="name"
                        name="name"
                        type="text"
                        value={values.name}
                        onChange={handleChange}
                        required
                        className={`${inputBase} font-customFont1`}
                        placeholder="Kirjoita nimesi.."
                        autoComplete="name"
                        aria-invalid={!!formErrors.name}
                        aria-describedby={formErrors.name ? "name-err" : undefined}
                        whileFocus={{ boxShadow: "0 0 0 3px rgba(49,255,217,0.35)" }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                      {formErrors.name && <p id="name-err" className="mt-1 text-xs text-pink-400">{formErrors.name}</p>}
                    </div>

                    {/* Sähköposti */}
                   <div>
                      <label htmlFor="email" className={labelBase}>Sähköposti *</label>
                      <motion.input
                        id="email"
                        name="email"
                        type="email"
                        value={values.email}
                        onChange={handleChange}
                        required
                        className={`${inputBase} font-customFont1`}
                        placeholder="sina@esimerkki.fi"
                        autoComplete="email"
                        aria-invalid={!!formErrors.email}
                        aria-describedby={formErrors.email ? "email-err" : undefined}
                        whileFocus={{ boxShadow: "0 0 0 3px rgba(49,255,217,0.35)" }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                      {formErrors.email && <p id="email-err" className="mt-1 text-xs text-pink-400">{formErrors.email}</p>}
                    </div>

                    {/* Puhelin */}
                   <div>
                    <label htmlFor="phone" className={labelBase}>Puhelinnumero</label>
                    <motion.input
                      id="phone"
                      name="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={values.phone}
                      onChange={handleChange}
                      className={`${inputBase} font-customFont1`}
                      placeholder="Esim. 044 123 4567"
                      aria-invalid={!!formErrors.phone}
                      aria-describedby={formErrors.phone ? "phone-err" : undefined}
                      whileFocus={{ boxShadow: "0 0 0 3px rgba(49,255,217,0.35)" }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    />
                    {formErrors.phone && <p id="phone-err" className="mt-1 text-xs text-pink-400">{formErrors.phone}</p>}
                  </div>


                    {/* Paikkakunta */}
                    <div>
                      <label htmlFor="city" className={labelBase}>Paikkakunta</label>
                      <motion.input
                        id="city"
                        name="city"
                        type="text"
                        value={values.city}
                        onChange={handleChange}
                        className={`${inputBase} font-customFont1`}
                        placeholder="Esim. Helsinki"
                        autoComplete="address-level2"
                        whileFocus={{ boxShadow: "0 0 0 3px rgba(49,255,217,0.35)" }}
                        transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      />
                    </div>

                    {/* Viesti */}
                   <div className="sm:col-span-2">
                      <label htmlFor="message" className={labelBase}>Viesti *</label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        required
                        minLength={10}
                        value={values.message}
                        onChange={(e) => {
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                          handleChange(e);
                        }}
                        className={`${textareaBase} resize-y min-h-[140px] font-customFont1`}
                        placeholder="Kerro lyhyesti asiasta.."
                        aria-invalid={!!formErrors.message}
                        aria-describedby={formErrors.message ? "message-err" : undefined}
                      />
                      {formErrors.message && <p id="message-err" className="mt-1 text-xs text-pink-400">{formErrors.message}</p>}
                    </div>
                  </div>

                  {!BYPASS_RECAPTCHA && (
                    <div className="mt-4">
                      <RecaptchaField
                        siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                        onChange={(val) => setRecaptchaValue(val)}
                        innerRef={recaptchaRef}
                      />
                    </div>
                  )}

                  <div className="mt-6 flex items-center gap-3">
                    <NeonButton type="submit" variant="primary" size="sm" IconLeft={Send} disabled={busy} aria-busy={busy}>
                      {busy ? "Lähetetään…" : "Lähetä viesti"}
                    </NeonButton>
                  </div>

                </form>
              </div>
            </article>
          </section>
        </div>
      </main>
    </>
  );
}
